import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ContentContainer } from '@/components/shell/ContentContainer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError, NetworkError } from '@/lib/api-client';
import { useAuthStore } from '@/stores/authStore';
import { STRINGS } from '@/strings/strings';

const SUCCESS_REDIRECT_DELAY_MS = 2000;

type FormErrorVariant = 'network' | 'save' | null;

/**
 * A-04 Mein Profil — parent firstName/lastName edit (Batch 9C, folds
 * in PWA/Offen #31 from Batch 9B Gate-0).
 *
 * Reuses `authStore.updateProfile` (existing PATCH /api/users/me) —
 * same helper Onboarding's ChildProfileRoute calls in its 2-step
 * submit. No backend extension needed.
 *
 * Success UX (Flag 3): inline success message + 2s setTimeout +
 * auto-redirect to /account. No success-modal — too heavyweight for
 * a 2-field form.
 */
export function ProfileRoute() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const updateProfile = useAuthStore((s) => s.updateProfile);

  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [errFirstName, setErrFirstName] = useState<string | null>(null);
  const [errLastName, setErrLastName] = useState<string | null>(null);
  const [formError, setFormError] = useState<FormErrorVariant>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const S = STRINGS.account.profile;

  // Auto-redirect after success
  useEffect(() => {
    if (!success) return;
    const t = window.setTimeout(() => {
      navigate('/account');
    }, SUCCESS_REDIRECT_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [success, navigate]);

  const validateRequired = (value: string): string | null =>
    value.trim() ? null : S.errors.required;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (success) return;

    const errF = validateRequired(firstName);
    const errL = validateRequired(lastName);
    setErrFirstName(errF);
    setErrLastName(errL);
    if (errF || errL) return;

    setFormError(null);
    setIsSubmitting(true);
    try {
      await updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      setSuccess(true);
    } catch (err) {
      if (err instanceof NetworkError) {
        setFormError('network');
      } else if (err instanceof ApiError) {
        setFormError('save');
      } else {
        setFormError('save');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const formErrorMessage =
    formError === 'network'
      ? S.errors.networkError
      : formError === 'save'
        ? S.errors.saveFailed
        : null;

  return (
    <div className="min-h-screen bg-dream-white">
      <ContentContainer maxWidth="narrow">
        <div className="py-6">
          <button
            type="button"
            onClick={() => navigate('/account')}
            className="mb-4 text-sm text-dream-navy/70 hover:text-dream-navy transition-colors"
          >
            {S.back}
          </button>

          <h1 className="text-[22px] font-semibold text-dream-navy mb-6">{S.title}</h1>

          {success && (
            <div
              role="status"
              aria-live="polite"
              data-testid="profile-success-message"
              className="flex items-center gap-2 mb-4 p-3 rounded-md bg-[color:var(--color-success)]/10 border border-[color:var(--color-success)]/30 text-[color:var(--color-success)] text-sm"
            >
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>{S.success}</span>
            </div>
          )}

          {formErrorMessage && (
            <div
              role="alert"
              className="flex items-start gap-2 mb-4 p-3 rounded-md bg-error/10 border border-error/30 text-error-text text-sm"
            >
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{formErrorMessage}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="profile-first-name" className="text-sm text-dream-navy">
                {S.firstNameLabel}
              </Label>
              <Input
                id="profile-first-name"
                type="text"
                autoComplete="given-name"
                value={firstName}
                maxLength={100}
                disabled={isSubmitting || success}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  if (errFirstName) setErrFirstName(null);
                }}
                onBlur={() => setErrFirstName(validateRequired(firstName))}
                aria-invalid={Boolean(errFirstName)}
                className="h-11 bg-dream-white text-dream-navy"
              />
              {errFirstName && (
                <p className="text-xs text-[color:var(--color-error)]">{errFirstName}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="profile-last-name" className="text-sm text-dream-navy">
                {S.lastNameLabel}
              </Label>
              <Input
                id="profile-last-name"
                type="text"
                autoComplete="family-name"
                value={lastName}
                maxLength={100}
                disabled={isSubmitting || success}
                onChange={(e) => {
                  setLastName(e.target.value);
                  if (errLastName) setErrLastName(null);
                }}
                onBlur={() => setErrLastName(validateRequired(lastName))}
                aria-invalid={Boolean(errLastName)}
                className="h-11 bg-dream-white text-dream-navy"
              />
              {errLastName && (
                <p className="text-xs text-[color:var(--color-error)]">{errLastName}</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={isSubmitting || success}
              className="w-full h-12 text-base font-semibold"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isSubmitting ? S.submitLoading : S.submit}
            </Button>
          </form>
        </div>
      </ContentContainer>
    </div>
  );
}
